import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function Hero3D() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x050505, 8, 22);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    camera.position.set(0, 0, 8);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const l1 = new THREE.PointLight(0xff4500, 8, 30); l1.position.set(6, 4, 4);  scene.add(l1);
    const l2 = new THREE.PointLight(0xdfff00, 6, 30); l2.position.set(-6, -3, -3); scene.add(l2);
    const l3 = new THREE.PointLight(0x00e5ff, 4, 30); l3.position.set(0, 5, -6); scene.add(l3);

    // Group
    const group = new THREE.Group();
    scene.add(group);

    // Central reactor (icosahedron)
    const coreGeo = new THREE.IcosahedronGeometry(1.15, 1);
    const coreMat = new THREE.MeshStandardMaterial({
      color: 0xff4500,
      roughness: 0.25,
      metalness: 0.9,
      emissive: 0xff4500,
      emissiveIntensity: 0.18,
      flatShading: true,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    group.add(core);

    // Wireframe shell around core
    const shell = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.6, 1),
      new THREE.MeshBasicMaterial({ color: 0xdfff00, wireframe: true, transparent: true, opacity: 0.18 })
    );
    group.add(shell);

    // Satellite glass panels
    const panels = [];
    const ringCount = 8;
    for (let i = 0; i < ringCount; i++) {
      const angle = (i / ringCount) * Math.PI * 2;
      const r = 3.2;
      const yOff = Math.sin(angle * 1.3) * 1.4;
      const mat = new THREE.MeshPhysicalMaterial({
        color: i % 3 === 0 ? 0xdfff00 : 0x1a1a1a,
        metalness: 0.6,
        roughness: 0.2,
        transmission: i % 3 === 0 ? 0 : 0.55,
        thickness: 0.4,
        clearcoat: 1,
        emissive: i % 3 === 0 ? 0xdfff00 : 0x000000,
        emissiveIntensity: i % 3 === 0 ? 0.35 : 0,
      });
      const panel = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.55, 0.06), mat);
      panel.position.set(Math.cos(angle) * r, Math.sin(angle) * 1.4, yOff);
      panel.rotation.set(Math.random() * 0.4, Math.random() * 0.4, Math.random() * 0.4);
      panel.userData = { angle, baseY: panel.position.y, baseZ: panel.position.z, phase: i * 0.7 };
      group.add(panel);
      panels.push(panel);
    }

    // Orbit rings
    const ring1 = new THREE.Mesh(
      new THREE.TorusGeometry(3.3, 0.005, 16, 220),
      new THREE.MeshBasicMaterial({ color: 0xff4500, transparent: true, opacity: 0.5 })
    );
    ring1.rotation.x = Math.PI / 2.3;
    group.add(ring1);

    const ring2 = new THREE.Mesh(
      new THREE.TorusGeometry(4.0, 0.004, 16, 220),
      new THREE.MeshBasicMaterial({ color: 0xdfff00, transparent: true, opacity: 0.28 })
    );
    ring2.rotation.x = Math.PI / 1.6;
    ring2.rotation.z = 0.4;
    group.add(ring2);

    // Particle field
    const particleCount = 260;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 22;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 16;
    }
    const partGeo = new THREE.BufferGeometry();
    partGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const partMat = new THREE.PointsMaterial({
      color: 0xdfff00,
      size: 0.03,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
    });
    const particles = new THREE.Points(partGeo, partMat);
    scene.add(particles);

    // Mouse parallax
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onPointer = (e) => {
      const rect = container.getBoundingClientRect();
      mouse.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 0.6;
      mouse.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 0.4;
    };
    container.addEventListener("pointermove", onPointer);

    // Animate
    const clock = new THREE.Clock();
    let rafId = 0;
    const tick = () => {
      const t = clock.getElapsedTime();
      mouse.x += (mouse.tx - mouse.x) * 0.06;
      mouse.y += (mouse.ty - mouse.y) * 0.06;

      group.rotation.y = t * 0.12 + mouse.x * 0.6;
      group.rotation.x = Math.sin(t * 0.18) * 0.08 + mouse.y * 0.4;

      core.rotation.y = t * 0.4;
      core.rotation.x = t * 0.25;
      shell.rotation.y = -t * 0.2;
      shell.rotation.x = t * 0.15;

      panels.forEach((p, i) => {
        const { angle, phase } = p.userData;
        p.position.y = Math.sin(t * 0.7 + phase) * 0.4 + Math.sin(angle) * 1.4;
        p.rotation.y += 0.004 + (i % 3) * 0.001;
        p.rotation.x += 0.002;
      });

      particles.rotation.y = t * 0.04;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };
    tick();

    // Resize
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      container.removeEventListener("pointermove", onPointer);
      renderer.dispose();
      coreGeo.dispose();
      partGeo.dispose();
      ring1.geometry.dispose();
      ring2.geometry.dispose();
      shell.geometry.dispose();
      panels.forEach((p) => { p.geometry.dispose(); p.material.dispose(); });
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="absolute inset-0" data-testid="hero-3d-canvas" />;
}
